import { createColumnHelper, type CellContext } from '@tanstack/react-table';
import { MoreHorizontalIcon } from 'lucide-react';
import { useMemo, useState } from 'react';

import { BillAction, type BillDTO, type BillHoldDTO } from '@invenco/accounts-interface';

import {
  AlertDialogWithAction,
  AlertDialogWithActionCancel,
  AlertDialogWithActionContent,
  AlertDialogWithActionDescription,
  AlertDialogWithActionFooter,
  AlertDialogWithActionHeader,
  AlertDialogWithActionSubmit,
  AlertDialogWithActionTitle,
} from '@/components/alert-dialog-with-action';
import { Button } from '@/components/core/button';
import { Card, CardAction, CardHeader } from '@/components/core/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/core/dropdown-menu';
import {
  DataTable,
  DataTableCardBody,
  DataTableCardTitle,
  useDataTable,
} from '@/components/data-table';
import { FormDialog } from '@/components/forms/form-dialog';
import { useGateways } from '@/gateways/GatewayProvider';
import { formatDate } from '@/shared/helpers';
import { Result } from '@/shared/helpers/Result';
import { useGatewayMutation } from '@/shared/hooks/queries';

import { HoldDialogContent } from './hold-dialog-content';

type Props = {
  bill?: BillDTO;
  isLoading: boolean;
  isError: boolean;
};

const columnHelper = createColumnHelper<BillHoldDTO>();

function HoldActions({
  hold,
  onRelease,
}: {
  hold: BillHoldDTO;
  onRelease: (hold: BillHoldDTO) => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon-sm" aria-label={`Open actions for hold: ${hold.reason}`}>
          <MoreHorizontalIcon />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" aria-label={`Actions for hold: ${hold.reason}`}>
        <DropdownMenuItem variant="destructive" onSelect={() => onRelease(hold)}>
          Release Hold
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const createHoldActionsCell = (onRelease: (hold: BillHoldDTO) => void) =>
  function HoldActionsCell({ row }: CellContext<BillHoldDTO, unknown>) {
    return <HoldActions hold={row.original} onRelease={onRelease} />;
  };

export function HoldsCard({ bill, isLoading, isError }: Props) {
  const { accountsGateway } = useGateways();
  const billId = bill?.id;
  const canAddHold = Boolean(bill?.allowableActions?.includes(BillAction.ADD_HOLD));

  // `undefined` (holds did not load) is handled by `hasError` below;
  // `[]` falls through to the empty state.
  const holds = useMemo(() => bill?.billHolds ?? [], [bill?.billHolds]);

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [releaseTarget, setReleaseTarget] = useState<BillHoldDTO | null>(null);

  const { mutate: addHold } = useGatewayMutation({
    mutationFn: (reason: string) => accountsGateway.addBillHold(billId!, reason),
    linkedQuery: ['bills', billId],
    successMessage: 'Hold added',
  });

  const { mutate: releaseHold } = useGatewayMutation({
    mutationFn: (holdId: string) => accountsGateway.releaseBillHold(billId!, holdId),
    linkedQuery: ['bills', billId],
    successMessage: 'Hold released',
  });

  const columns = useMemo(
    () => [
      columnHelper.accessor('reason', {
        header: 'Reason',
        meta: { align: 'left', truncate: true },
      }),
      columnHelper.accessor('createdAt', {
        header: 'Added on',
        cell: ({ getValue }) => formatDate(getValue()),
      }),
      columnHelper.display({
        id: 'actions',
        cell: createHoldActionsCell(setReleaseTarget),
        meta: { align: 'right' },
      }),
    ],
    [],
  );

  const table = useDataTable({
    data: holds,
    columns,
    isLoading: isLoading,
    // Bills rarely carry more than one hold, so a single placeholder row is enough.
    loadingRowCount: 1,
    hasError: !isLoading && (isError || (!!bill && bill.billHolds === undefined)),
    enableSorting: false,
  });

  return (
    <>
      <Card aria-label="Active holds">
        <CardHeader>
          <DataTableCardTitle table={table}>Active holds</DataTableCardTitle>
          {canAddHold ? (
            <CardAction>
              <Button
                size="sm"
                variant="outline"
                disabled={!billId}
                onClick={() => setIsAddOpen(true)}
              >
                New Hold
              </Button>
            </CardAction>
          ) : null}
        </CardHeader>
        <DataTableCardBody table={table} emptyMessage="No active holds">
          <DataTable table={table} />
        </DataTableCardBody>
      </Card>

      <FormDialog open={isAddOpen} onOpenChange={setIsAddOpen}>
        <HoldDialogContent
          onAdd={(reason) => {
            if (!billId) return Promise.resolve(Result.fail('Bill ID missing'));
            return addHold(reason);
          }}
        />
      </FormDialog>

      <AlertDialogWithAction
        open={releaseTarget !== null}
        onOpenChange={(open) => {
          if (!open) setReleaseTarget(null);
        }}
        onAction={() => {
          if (!releaseTarget || !billId) return Promise.resolve(Result.fail('No hold selected'));
          return releaseHold(releaseTarget.id);
        }}
      >
        <AlertDialogWithActionContent>
          <AlertDialogWithActionHeader>
            <AlertDialogWithActionTitle>
              {releaseTarget ? `Release the hold "${releaseTarget.reason}"?` : 'Release this hold?'}
            </AlertDialogWithActionTitle>
            <AlertDialogWithActionDescription>
              Releasing removes this hold from the bill. This cannot be undone.
            </AlertDialogWithActionDescription>
          </AlertDialogWithActionHeader>
          <AlertDialogWithActionFooter>
            <AlertDialogWithActionCancel>Cancel</AlertDialogWithActionCancel>
            <AlertDialogWithActionSubmit>Release</AlertDialogWithActionSubmit>
          </AlertDialogWithActionFooter>
        </AlertDialogWithActionContent>
      </AlertDialogWithAction>
    </>
  );
}
